import { postJson, postForm } from './common.js'

export async function getServiceOnboardingStatus() {
  const resJson = await postJson('/settings/get-service-onboarding-status', {});
  return resJson;
}

export async function getAccountProfile() {
  const resJson = await postJson('/settings/get-profile', {});
  if (!resJson.profile) throw Error('Malformed response from backend');
  return resJson.profile;
}

export async function updateAccountProfile(data) {
  const resJson = await postJson('/settings/set-profile', data);
  if (!resJson.profile) throw Error('Malformed response from backend');
  return resJson.profile;
}

export async function replaceAccountLogo(logoBlob, clear = false) {
  // Create FormData for multipart upload
  const formData = new FormData();

  if (clear) {
    // Send clear flag to delete logo
    formData.append('clear', 'true');
  } else if (logoBlob) {
    // Send new logo file
    formData.append('logo', logoBlob, 'account-logo.png');
  }

  const resJson = await postForm('/settings/replace-account-logo', formData);
  return {
    key: resJson.key
  };
}

export async function getContactMethods() {
  const resJson = await postJson('/settings/get-contact-methods', {});
  if (!resJson.methods) throw Error('Malformed response from backend');
  return resJson.methods;
}

export async function setContactMethods(methods) {
  const resJson = await postJson('/settings/set-contact-methods', { methods });
  if (!resJson.success) throw Error('Failed to update contact methods');
  return resJson;
}

export async function getEnabledServices() {
  const resJson = await postJson('/settings/get-enabled-services', {});
  if (!resJson.services) throw Error('Malformed response from backend');
  return resJson.services;
}

export async function updateEnabledServices(flags) {
  const resJson = await postJson('/settings/set-enabled-services', flags);
  if (!resJson.services) throw Error('Malformed response from backend');
  return resJson.services;
}

export async function getTelehealthSettings() {
  const resJson = await postJson('/settings/get-telehealth-settings', {});
  if (!resJson.settings) throw Error('Malformed response from backend');
  return resJson.settings;
}

export async function updateTelehealthSettings(data) {
  const resJson = await postJson('/settings/set-telehealth-settings', data);
  if (!resJson.settings) throw Error('Malformed response from backend');
  return resJson.settings;
}

export async function getMessagingSettings() {
  const resJson = await postJson('/settings/get-messaging-settings', {});
  if (!resJson.messaging) throw Error('Malformed response from backend');
  return resJson.messaging;
}

export async function updateMessagingSettings(data) {
  const resJson = await postJson('/settings/set-messaging-settings', data);
  if (!resJson.messaging) throw Error('Malformed response from backend');
  return resJson.messaging;
}

// ==================== Integration Settings APIs ====================

export async function getIntegrationSettings() {
  const resJson = await postJson('/settings/get-integration-settings', {});
  if (!resJson.integration) throw Error('Malformed response from backend');
  return resJson.integration;
}

export async function updateIntegrationSettings(data) {
  const resJson = await postJson('/settings/set-integration-settings', data);
  if (!resJson.integration) throw Error('Malformed response from backend');
  return resJson.integration;
}

// ==================== Per-EMR Integration Settings APIs ====================

export async function getAdvancedMdSettings() {
  const resJson = await postJson('/settings/emr/get-advancedmd', {});
  if (!resJson.advancedmd) throw Error('Malformed response from backend');
  return resJson.advancedmd;
}

export async function updateAdvancedMdSettings(data) {
  const resJson = await postJson('/settings/emr/set-advancedmd', data);
  if (!resJson.advancedmd) throw Error('Malformed response from backend');
  return resJson.advancedmd;
}

export async function getPracticeFusionSettings() {
  const resJson = await postJson('/settings/emr/get-practice-fusion', {});
  if (!resJson.practiceFusion) throw Error('Malformed response from backend');
  return resJson.practiceFusion;
}

export async function updatePracticeFusionSettings(data) {
  const resJson = await postJson('/settings/emr/set-practice-fusion', data);
  if (!resJson.practiceFusion) throw Error('Malformed response from backend');
  return resJson.practiceFusion;
}

export async function getAthenahealthSettings() {
  const resJson = await postJson('/settings/emr/get-athenahealth', {});
  if (!resJson.athenahealth) throw Error('Malformed response from backend');
  return resJson.athenahealth;
}

export async function updateAthenahealthSettings(data) {
  const resJson = await postJson('/settings/emr/set-athenahealth', data);
  if (!resJson.athenahealth) throw Error('Malformed response from backend');
  return resJson.athenahealth;
}

export async function getEclinicalworksSettings() {
  const resJson = await postJson('/settings/emr/get-eclinicalworks', {});
  if (!resJson.eclinicalworks) throw Error('Malformed response from backend');
  return resJson.eclinicalworks;
}

export async function updateEclinicalworksSettings(data) {
  const resJson = await postJson('/settings/emr/set-eclinicalworks', data);
  if (!resJson.eclinicalworks) throw Error('Malformed response from backend');
  return resJson.eclinicalworks;
}

// ==================== Faxing Settings APIs ====================

export async function getFaxingSettings() {
  const resJson = await postJson('/settings/get-faxing-settings', {});
  if (!resJson.faxing) throw Error('Malformed response from backend');
  return resJson.faxing;
}

export async function updateFaxingSettings(data) {
  const resJson = await postJson('/settings/set-faxing-settings', data);
  if (!resJson.faxing) throw Error('Malformed response from backend');
  return resJson.faxing;
}

// ==================== Forms Settings APIs ====================

export async function getFormsSettings() {
  const resJson = await postJson('/settings/get-forms-settings', {});
  if (!resJson.forms) throw Error('Malformed response from backend');
  return resJson.forms;
}

export async function updateFormsSettings(data) {
  const resJson = await postJson('/settings/set-forms-settings', data);
  if (!resJson.forms) throw Error('Malformed response from backend');
  return resJson.forms;
}

// ==================== Reminder Settings APIs ====================

export async function getReminderSettings() {
  const resJson = await postJson('/settings/get-reminder-settings', {});
  if (resJson.reminders === undefined) throw Error('Malformed response from backend');
  return {
    reminders: resJson.reminders,
    isConnected: resJson.isConnected || false,
    isSyncingEnabled: resJson.isSyncingEnabled || false,
    isSendingEnabled: resJson.isSendingEnabled || false,
    timeZone: resJson.timeZone || 'America/New_York',
    dateLastSync: resJson.dateLastSync || null,
    syncAvailableAt: resJson.syncAvailableAt || null,
  };
}

export async function getReminderEnums(bypassCache = false) {
  const resJson = await postJson('/settings/get-reminder-enums', { bypassCache });
  if (!resJson.appointmentTypes || !resJson.facilities) throw Error('Malformed response from backend');
  return {
    appointmentTypes: resJson.appointmentTypes,
    facilities: resJson.facilities
  };
}

export async function setReminderSettings(data) {
  const resJson = await postJson('/settings/set-reminder-settings', data);
  if (resJson.reminders === undefined) throw Error('Malformed response from backend');
  return {
    reminders: resJson.reminders,
    isConnected: resJson.isConnected || false,
    isSyncingEnabled: resJson.isSyncingEnabled || false,
    isSendingEnabled: resJson.isSendingEnabled || false,
    timeZone: resJson.timeZone || 'America/New_York',
    dateLastSync: resJson.dateLastSync || null,
  };
}

export async function getUpcomingReminders(reminderId = null) {
  const body = {};
  if (reminderId) body.reminderId = reminderId;
  const resJson = await postJson('/settings/list-upcoming-reminders', body);
  if (!resJson.result) throw Error('Malformed response from backend');
  return resJson.result;
}

export async function connectReminders(data) {
  const resJson = await postJson('/settings/connect-reminders', data);
  if (!resJson.message) throw Error('Malformed response from backend');
  return resJson;
}

export async function disconnectReminders() {
  const resJson = await postJson('/settings/disconnect-reminders', {});
  if (!resJson.message) throw Error('Malformed response from backend');
  return resJson;
}

export async function syncAppointmentsReminders() {
  const resJson = await postJson('/settings/sync-appointments', {});
  if (!resJson.message) throw Error('Malformed response from backend');
  return resJson;
}

export async function listAppointmentsLocal(fromDate, toDate) {
  const resJson = await postJson('/settings/list-appointments-local', { fromDate, toDate });
  if (!resJson.appointments) throw Error('Malformed response from backend');
  return resJson.appointments;
}

// ==================== Billing Settings APIs ====================

export async function getBillingInfo() {
  const resJson = await postJson('/settings/get-billing-info', {});
  return resJson;
}

export async function createBillingSetupIntent() {
  const resJson = await postJson('/settings/create-billing-setup-intent', {});
  if (!resJson.clientSecret) throw Error('Malformed response from backend');
  return resJson.clientSecret;
}

export async function setDefaultPaymentMethod(paymentMethodId) {
  const resJson = await postJson('/settings/set-default-payment-method', { paymentMethodId });
  return resJson;
}
